import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import '@/styles/permissionsStyles.scss';
import './UnifiedEndpointManagementPage.scss';
import {
    FaSearch,
    FaDesktop,
    FaCheckCircle,
    FaExclamationTriangle,
    FaTimesCircle,
    FaUsers,
    FaChevronLeft,
    FaChevronRight
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

import UserRow from './UserRow';
import ScriptRunnerModal from './ScriptRunnerModal';
import DeviceDetailModal from './DeviceDetailModal';
import PasswordRevealModal from './PasswordRevealModal';

const PAGE_SIZE = 25;

const UnifiedEndpointManagementPage = () => {
    const { apiFetch } = useAuth();
    const [searchParams] = useSearchParams();

    // ── State ─────────────────────────────────────────────────────────────────
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    // Expanded user detail
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [expandedDevices, setExpandedDevices] = useState([]);
    const [expandedSecurity, setExpandedSecurity] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // Action loading states
    const [actionLoading, setActionLoading] = useState({});

    // Script Runner state
    const [scriptModalOpen, setScriptModalOpen] = useState(false);
    const [scriptModalDevice, setScriptModalDevice] = useState(null);
    const [scriptTemplates, setScriptTemplates] = useState([]);
    const [scriptCategories, setScriptCategories] = useState([]);

    // Device Detail state
    const [deviceModalOpen, setDeviceModalOpen] = useState(false);
    const [deviceModalDevice, setDeviceModalDevice] = useState(null);
    const [deviceDetail, setDeviceDetail] = useState(null);
    const [deviceHealth, setDeviceHealth] = useState(null);
    const [deviceDetailLoading, setDeviceDetailLoading] = useState(false);

    // Password Reveal Modal state
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [tempPassword, setTempPassword] = useState(null);
    const [passwordResetUser, setPasswordResetUser] = useState('');

    // Filter state
    const [statusFilter, setStatusFilter] = useState('enabled');
    const [linkedFilter, setLinkedFilter] = useState('all');

    const searchInputRef = useRef(null);

    // ── Debounced search ─────────────────────────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(prev => {
                const newVal = searchQuery;
                if (prev !== newVal) setPage(1);
                return newVal;
            });
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // ── Load users ───────────────────────────────────────────────────────────
    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: PAGE_SIZE.toString()
            });
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (statusFilter !== 'all') params.set('accountStatus', statusFilter);
            if (linkedFilter !== 'all') params.set('linkStatus', linkedFilter);

            const res = await apiFetch(`/api/UnifiedEndpoint/users?${params}`);
            if (res.ok) {
                const data = await res.json();
                setUsers(data.users || []);
                setTotalCount(data.totalCount || 0);
                setHasMore(data.hasMore || false);
            } else {
                try {
                    const errData = await res.json();
                    toast.error(`Failed to load users: ${errData.detail || errData.error || res.statusText}`);
                    console.error('UnifiedEndpoint error:', errData);
                } catch {
                    toast.error('Failed to load users.');
                }
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load users.');
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [apiFetch, page, debouncedSearch, statusFilter, linkedFilter]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Seed the search box from the ?search= query param when navigated from
    // another page (e.g., the "Open in Endpoint Management" button on an IT
    // action item's Endpoint Diagnostics tab). Runs once on mount.
    useEffect(() => {
        const initial = searchParams.get('search');
        if (initial && initial.trim()) {
            setSearchQuery(initial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Expand user detail ───────────────────────────────────────────────────
    const toggleUserExpand = async (entraUserId) => {
        if (expandedUserId === entraUserId) {
            setExpandedUserId(null);
            setExpandedDevices([]);
            setExpandedSecurity(null);
            return;
        }

        setExpandedUserId(entraUserId);
        setLoadingDetail(true);
        setExpandedDevices([]);
        setExpandedSecurity(null);

        try {
            const [devRes, secRes] = await Promise.all([
                apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}/devices`),
                apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}/security`)
            ]);

            if (devRes.ok) {
                const devData = await devRes.json();
                setExpandedDevices(devData || []);
            }
            if (secRes.ok) {
                const secData = await secRes.json();
                setExpandedSecurity(secData);
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load user details.');
        } finally {
            setLoadingDetail(false);
        }
    };

    // ── Actions ──────────────────────────────────────────────────────────────
    const setActionState = (key, isLoading) => {
        setActionLoading(prev => ({ ...prev, [key]: isLoading }));
    };

    const handleSyncDevice = async (deviceId) => {
        setActionState(`sync-${deviceId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/devices/${deviceId}/sync`, { method: 'POST' });
            const data = await res.json();
            if (data.success) toast.success('Sync initiated.');
            else toast.error(data.message || 'Sync failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Sync request failed.');
        } finally {
            setActionState(`sync-${deviceId}`, false);
        }
    };

    const handleRebootDevice = async (deviceId) => {
        if (!confirm('Are you sure you want to reboot this device?')) return;
        setActionState(`reboot-${deviceId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/devices/${deviceId}/reboot`, { method: 'POST' });
            const data = await res.json();
            if (data.success) toast.success('Reboot initiated.');
            else toast.error(data.message || 'Reboot failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Reboot request failed.');
        } finally {
            setActionState(`reboot-${deviceId}`, false);
        }
    };

    const handleConnectDevice = async (deviceName, streamerId, wifiMac, ethernetMac) => {
        setActionState(`connect-${deviceName}`, true);
        try {
            const mac = (wifiMac || ethernetMac || '').replace(/[:-]/g, '').toUpperCase();
            if (mac) {
                const uri = `st-business://com.splashtop.business?mac=${mac}`;
                window.location.href = uri;
                toast.success(`Launching Splashtop connection to ${deviceName}...`, { autoClose: 4000 });
            } else {
                await navigator.clipboard.writeText(deviceName);
                toast.success(`Device name "${deviceName}" copied to clipboard. Search for it in the Splashtop Business app.`, { autoClose: 6000 });
            }
        } catch {
            toast.info(`Device name: ${deviceName} — search for this in the Splashtop Business app.`, { autoClose: 8000 });
        } finally {
            setActionState(`connect-${deviceName}`, false);
        }
    };

    const handleDisableUser = async (entraUserId, displayName) => {
        if (!confirm(`Disable account for ${displayName}? They will be unable to sign in.`)) return;
        setActionState(`disable-${entraUserId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}/disable`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                toast.success('Account disabled.');
                loadUsers();
            } else toast.error(data.message || 'Failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Disable failed.');
        } finally {
            setActionState(`disable-${entraUserId}`, false);
        }
    };

    const handleEnableUser = async (entraUserId) => {
        setActionState(`enable-${entraUserId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}/enable`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                toast.success('Account enabled.');
                loadUsers();
            } else toast.error(data.message || 'Failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Enable failed.');
        } finally {
            setActionState(`enable-${entraUserId}`, false);
        }
    };

    const handleRevokeSessionsUser = async (entraUserId, displayName) => {
        if (!confirm(`Revoke all sessions for ${displayName}?`)) return;
        setActionState(`revoke-${entraUserId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}/revoke-sessions`, { method: 'POST' });
            const data = await res.json();
            if (data.success) toast.success('All sessions revoked.');
            else toast.error(data.message || 'Failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Revoke sessions failed.');
        } finally {
            setActionState(`revoke-${entraUserId}`, false);
        }
    };

    const handleResetPassword = async (entraUserId, displayName) => {
        if (!confirm(`Reset password for ${displayName}? A temporary password will be generated.`)) return;
        setActionState(`reset-${entraUserId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}/reset-password`, { method: 'POST' });
            const data = await res.json();
            if (data.success && data.temporaryPassword) {
                setTempPassword(data.temporaryPassword);
                setPasswordResetUser(displayName);
                setPasswordModalOpen(true);
            } else if (data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message || 'Failed.');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Password reset failed.');
        } finally {
            setActionState(`reset-${entraUserId}`, false);
        }
    };

    // ── Script Runner ─────────────────────────────────────────────────────
    const openScriptRunner = async (device) => {
        setScriptModalDevice(device);
        setScriptModalOpen(true);

        if (scriptTemplates.length === 0) {
            try {
                const res = await apiFetch('/api/UnifiedEndpoint/scripts/templates');
                if (res.ok) {
                    const data = await res.json();
                    setScriptTemplates(data.templates || []);
                    setScriptCategories(data.categories || []);
                }
            } catch (err) {
                Sentry.captureException(err);
                toast.error('Failed to load script templates.');
            }
        }
    };

    // ── Device Detail ────────────────────────────────────────────────────────
    const openDeviceDetail = async (device) => {
        setDeviceModalDevice(device);
        setDeviceDetail(null);
        setDeviceHealth(null);
        setDeviceModalOpen(true);
        setDeviceDetailLoading(true);

        try {
            const [detailRes, healthRes] = await Promise.all([
                apiFetch(`/api/UnifiedEndpoint/devices/${device.deviceId}/detail`),
                apiFetch(`/api/UnifiedEndpoint/devices/${device.deviceId}/health`)
            ]);
            if (detailRes.ok) setDeviceDetail(await detailRes.json());
            if (healthRes.ok) setDeviceHealth(await healthRes.json());
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load device details.');
        } finally {
            setDeviceDetailLoading(false);
        }
    };

    // ── Helpers ──────────────────────────────────────────────────────────────
    const handleUpdateUser = async (entraUserId, fields) => {
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(fields)
            });
            const data = await res.json();
            if (data.success) {
                toast.success('User updated successfully.');
                setUsers(prev => prev.map(u => u.entraUserId === entraUserId
                    ? { ...u, ...fields }
                    : u
                ));
            } else {
                toast.error(data.message || 'Update failed.');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to update user.');
        }
    };

    const handleSetManager = async (entraUserId, displayName) => {
        const managerEmail = prompt(`Enter the email or UPN of the new manager for ${displayName}:`);
        if (!managerEmail) return;

        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/users?search=${encodeURIComponent(managerEmail)}&page=1&pageSize=5`);
            if (!res.ok) { toast.error('Failed to search for manager.'); return; }
            const data = await res.json();
            const match = (data.users || []).find(u =>
                u.mail?.toLowerCase() === managerEmail.toLowerCase() ||
                u.userPrincipalName?.toLowerCase() === managerEmail.toLowerCase()
            );
            if (!match) { toast.error(`No Entra user found matching "${managerEmail}".`); return; }

            const setRes = await apiFetch(`/api/UnifiedEndpoint/users/${entraUserId}/set-manager`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ managerUserId: match.entraUserId })
            });
            const setData = await setRes.json();
            if (setData.success) {
                toast.success(`Manager set to ${match.displayName}.`);
            } else {
                toast.error(setData.message || 'Failed to set manager.');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to set manager.');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const getComplianceBadge = (state) => {
        if (!state) return <span className="uem-badge uem-badge--unknown">Unknown</span>;
        const s = state.toLowerCase();
        if (s === 'compliant') return <span className="uem-badge uem-badge--success"><FaCheckCircle /> Compliant</span>;
        if (s === 'noncompliant') return <span className="uem-badge uem-badge--danger"><FaTimesCircle /> Non-Compliant</span>;
        return <span className="uem-badge uem-badge--warning"><FaExclamationTriangle /> {state}</span>;
    };

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="uem-page">
            <div className="main-content">
                <h1 className="uem-title">
                    <FaDesktop className="uem-title-icon" />
                    Unified Endpoint Management
                </h1>
                <p className="uem-subtitle">
                    Centralised control across Entra ID, Intune, Defender, SharePoint &amp; Splashtop
                </p>

                {/* Toolbar */}
                <div className="uem-toolbar">
                    <div className="uem-search-wrapper">
                        <FaSearch className="uem-search-icon" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="uem-search-input"
                            placeholder="Search by name, email, department, job title..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button type="button" className="uem-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">×</button>
                        )}
                    </div>

                    <div className="uem-filters">
                        <select
                            className="uem-filter-select"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        >
                            <option value="all">All Accounts</option>
                            <option value="enabled">Enabled Only</option>
                            <option value="disabled">Disabled Only</option>
                        </select>

                        <select
                            className="uem-filter-select"
                            value={linkedFilter}
                            onChange={(e) => { setLinkedFilter(e.target.value); setPage(1); }}
                        >
                            <option value="all">All Links</option>
                            <option value="linked">CapTrac Linked</option>
                            <option value="unlinked">Not Linked</option>
                        </select>
                    </div>

                    <div className="uem-toolbar-stats">
                        <span className="uem-stat"><FaUsers /> {totalCount} users</span>
                    </div>
                </div>

                {/* Table */}
                {loading && initialLoad ? (
                    <HexagonLoader />
                ) : (
                    <>
                        <div className="uem-table-container">
                            <table className="uem-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>NAME</th>
                                        <th>EMAIL</th>
                                        <th>JOB TITLE</th>
                                        <th>DEPARTMENT</th>
                                        <th>STATUS</th>
                                        <th>LAST SIGN-IN</th>
                                        <th>CAPTRAC LINK</th>
                                        <th>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="uem-empty">
                                                No users found. {debouncedSearch ? 'Try a different search term.' : ''}
                                            </td>
                                        </tr>
                                    ) : users.map(u => (
                                        <UserRow
                                            key={u.entraUserId}
                                            user={u}
                                            isExpanded={expandedUserId === u.entraUserId}
                                            onToggle={() => toggleUserExpand(u.entraUserId)}
                                            loadingDetail={loadingDetail && expandedUserId === u.entraUserId}
                                            devices={expandedUserId === u.entraUserId ? expandedDevices : []}
                                            security={expandedUserId === u.entraUserId ? expandedSecurity : null}
                                            actionLoading={actionLoading}
                                            formatDate={formatDate}
                                            getComplianceBadge={getComplianceBadge}
                                            onSyncDevice={handleSyncDevice}
                                            onRebootDevice={handleRebootDevice}
                                            onConnectDevice={handleConnectDevice}
                                            onDisableUser={handleDisableUser}
                                            onEnableUser={handleEnableUser}
                                            onRevokeSessionsUser={handleRevokeSessionsUser}
                                            onResetPassword={handleResetPassword}
                                            onUpdateUser={handleUpdateUser}
                                            onSetManager={handleSetManager}
                                            onOpenScriptRunner={openScriptRunner}
                                            onOpenDeviceDetail={openDeviceDetail}
                                            apiFetch={apiFetch}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="uem-pagination">
                                <button
                                    type="button"
                                    className="uem-page-btn"
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                >
                                    <FaChevronLeft /> Previous
                                </button>
                                <span className="uem-page-info">
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    type="button"
                                    className="uem-page-btn"
                                    disabled={!hasMore}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    Next <FaChevronRight />
                                </button>
                            </div>
                        )}
                    </>
                )}

                <ScriptRunnerModal
                    isOpen={scriptModalOpen}
                    onClose={() => setScriptModalOpen(false)}
                    device={scriptModalDevice}
                    templates={scriptTemplates}
                    categories={scriptCategories}
                    apiFetch={apiFetch}
                />

                <DeviceDetailModal
                    isOpen={deviceModalOpen}
                    onClose={() => setDeviceModalOpen(false)}
                    device={deviceModalDevice}
                    detail={deviceDetail}
                    health={deviceHealth}
                    loading={deviceDetailLoading}
                    apiFetch={apiFetch}
                    formatDate={formatDate}
                />

                <PasswordRevealModal
                    isOpen={passwordModalOpen}
                    onClose={() => { setPasswordModalOpen(false); setTempPassword(null); }}
                    password={tempPassword}
                    userName={passwordResetUser}
                />
            </div>
        </div>
    );
};

export default UnifiedEndpointManagementPage;
